import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '../components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { publicHealthCheck } from '../services/api';
import { ThemeToggleIconOnly } from '../components/common/theme-toggle';

const setupSchema = z.object({
  secret: z.string().min(1, 'Setup secret is required'),
  email: z.string().email('Invalid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  name: z.string().optional(),
});

export function SetupScreen() {
  const { setupSuperAdmin, loading, error, clearError } = useAuth();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [setupAvailable, setSetupAvailable] = useState(true);

  const form = useForm<z.infer<typeof setupSchema>>({
    resolver: zodResolver(setupSchema),
    mode: 'onChange',
    defaultValues: {
      secret: '',
      email: '',
      password: '',
      name: '',
    },
  });

  // Initialize dark mode on setup page
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    // Check if setup is already done by trying the health endpoint
    const checkSetup = async () => {
      try {
        // Try health endpoint
        await publicHealthCheck();
        setSetupAvailable(true);
      } catch {
        // If health check fails, server might be down
        // Still show setup form
        setSetupAvailable(true);
      }
    };

    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    checkSetup();
    clearError();
  }, [isAuthenticated, navigate, clearError]);

  const onSubmit = async (data: any) => {
    try {
      await setupSuperAdmin(data);
      // Success - authStore will redirect to dashboard
    } catch (error: any) {
      // Check if setup is already completed
      // The error message from API will be "Setup already completed" or similar
      if (error?.message?.toLowerCase().includes('already')) {
        navigate('/login');
      }
      // Other errors are handled by the store and displayed in the UI
    }
  };

  if (!setupAvailable) {
    return (
      <div className="flex h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
              <CardTitle>Setup Unavailable</CardTitle>
            </div>
            <CardDescription>
              Super admin has already been configured. Please login instead.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
              <CardTitle>Initial Setup</CardTitle>
            </div>
            <ThemeToggleIconOnly />
          </div>
          <CardDescription>
            Create your super admin account to get started
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="secret"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Setup Secret</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="Enter setup secret" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="admin@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Super Admin" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Min 8 chars, 1 uppercase, 1 lowercase, 1 number
                    </p>
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Super Admin'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
